// Form meta & SQL table columns — the form identity tile, the version-derived
// publish state, the created/updated
// timestamps and person, the entry count, and the two SQL cells read off the
// form's `form_vertical_export` entry (table name + whether the connection is
// live).
//
// NOTHING here declares `sortable`. POST /dashboard-setup/list-forms carries no
// orderBy, so a sort glyph would be a control that cannot work. Add flags (and
// an `onSort`) only once the payload names its accepted keys, and declare those
// keys as an enum rather than free strings.
//
// Enabled option sets append one column each, between the fixed columns and the
// row kebab — the dataSourceEntryColumns idiom for a runtime-varying column set.
// Their keys are prefixed so they can never collide with a fixed column's, and
// so the page can hand the same keys to useColumnVisibility as `excluded`:
// Manage sets is the control for those columns, and two controls for one
// concept is one too many.
//
// Four write surfaces, none of which mutate here — all report up to the page,
// which owns the drafts and the confirms: the table name typed into DbNameCell
// is a draft that DataConnectionCell's switch commits, and the trailing kebab
// re-imports a connected form's export. The kebab renders nothing on a row with
// no live export, so unconnected rows carry no affordance at all.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon, type DataTableColumn } from '@/components/common';
import type { DataSetupForm } from '@/api/endpoints/dataSetupApi';
import type { OptionSetSelection } from '@/api/endpoints/formSetsApi';
import type { FormPerson } from '@/api/endpoints/formsApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import i18n from '@/i18n/config';
import DbNameCell from './DbNameCell';
import DataConnectionCell from './DataConnectionCell';
import FormMetaRowActions from './FormMetaRowActions';
import SetValueCell from './SetValueCell';
import { fmtDateTime } from '@/utils/formatDate';

/** Column key for an enabled set. Prefixed so it can never collide with a fixed
 *  column's key, and so the page can recognise one. */
export const setColumnKey = (setId: string) => `set:${setId}`;

const dash = <span className='data-setup-table__muted'>—</span>;

const PersonCell = ({ person }: { person: FormPerson | null }) => {
  if (!person) return dash;
  return (
    <span className='data-setup-table__person'>
      <Avatar
        size={28}
        tone={toneOf(person.id || person.fullName)}
        initials={initialsOf(person.fullName)}
      />
      <span className='data-setup-table__person-name'>{person.fullName}</span>
    </span>
  );
};

interface UseFormMetaColumnsArgs {
  /** Draft if the user has typed one, else the persisted name, else ''. */
  dbNameOf: (form: DataSetupForm) => string;
  onDbNameCommit: (formId: string, next: string) => void;
  onToggleConnection: (form: DataSetupForm, next: boolean) => void;
  /** Row kebab's Re-import. Absent = the kebab renders nothing. */
  onReimport?: (form: DataSetupForm) => void;
  /** Enabled sets, in catalogue order — one appended column each. */
  sets: OptionSetSelection[];
  /** The drafted option id for a row+set, or '' for nothing picked. */
  setValueOf: (form: DataSetupForm, set: OptionSetSelection) => string;
  onSetValueCommit: (formId: string, setId: string, optionId: string) => void;
}

export const useFormMetaColumns = ({
  dbNameOf,
  onDbNameCommit,
  onToggleConnection,
  onReimport,
  sets,
  setValueOf,
  onSetValueCommit,
}: UseFormMetaColumnsArgs): DataTableColumn<DataSetupForm>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'form',
      label: t('data_setup_meta_col_form'),
      // The only fr column — long titles ellipsize here rather than widening
      // the whole table. Every other column hugs its content and scrolls.
      width: 'minmax(14rem, 1.6fr)',
      render: (f) => (
        <span className='data-setup-table__name'>
          <span className='data-setup-table__tile' aria-hidden='true'>
            <Icon name='clipboard-list' size={18} />
          </span>
          <span className='data-setup-table__title'>{f.title}</span>
        </span>
      ),
    },
    {
      key: 'state',
      label: t('data_setup_meta_col_status'),
      // Derived in the mapper: working version === published version. One chip,
      // never both — this list has no suspended arm (see DataSetupFormState).
      // Copy reuses the forms module's keys rather than duplicating the words.
      render: (f) =>
        f.state === 'published' ? (
          <Chip variant='success' iconStart='circle-check'>
            {t('forms_status_published')}
          </Chip>
        ) : (
          <Chip variant='neutral' iconStart='pencil'>
            {t('forms_status_draft')}
          </Chip>
        ),
    },
    {
      key: 'createdAt',
      label: t('data_setup_meta_col_created_at'),
      render: (f) => fmtDateTime(f.createdAt),
    },
    {
      key: 'createdBy',
      label: t('data_setup_meta_col_created_by'),
      render: (f) => <PersonCell person={f.createdBy} />,
    },
    {
      key: 'updatedAt',
      label: t('data_setup_meta_col_updated_at'),
      render: (f) => fmtDateTime(f.updatedAt),
    },
    {
      key: 'entries',
      label: t('data_setup_meta_col_entries'),
      align: 'end',
      render: (f) => (
        <span className='data-setup-table__entries'>{f.entries.toLocaleString(i18n.language)}</span>
      ),
    },
    {
      key: 'dbName',
      label: t('data_setup_meta_col_db_name'),
      render: (f) => <DbNameCell form={f} value={dbNameOf(f)} onCommit={onDbNameCommit} />,
    },
    {
      key: 'dbConnection',
      label: t('data_setup_meta_col_db_connection'),
      render: (f) => (
        <DataConnectionCell form={f} dbName={dbNameOf(f)} onToggle={onToggleConnection} />
      ),
    },
    ...sets.map<DataTableColumn<DataSetupForm>>((s) => ({
      key: setColumnKey(s.id),
      label: s.title,
      // A fixed track, not a hugging one: a native select's min-content is its
      // longest option, which on an `auto` track would widen the whole table.
      // The cell's `fitContainer` ellipsizes inside this width.
      width: '12rem',
      render: (f) => (
        <SetValueCell
          rowId={f.id}
          rowLabel={f.title}
          groupLabel={s.title}
          options={s.options}
          value={setValueOf(f, s)}
          // The cell reports (rowId, optionId); this column knows its own set.
          onChange={(formId, optionId) => onSetValueCommit(formId, s.id, optionId)}
        />
      ),
    })),
    {
      key: 'actions',
      label: '',
      width: '3rem',
      align: 'end',
      render: (f) => <FormMetaRowActions form={f} onReimport={onReimport} />,
    },
  ];
};

export default useFormMetaColumns;
